import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '../services/api';

interface Account {
  id: string;
  name: string;
  type: string;
  description?: string;
}

export default function AccountsPage() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [form, setForm] = useState({ name: '', type: 'bank', description: '' });

  const load = () => api.get<Account[]>('/api/accounts').then(setAccounts);

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', type: 'bank', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (acc: Account) => {
    setEditing(acc);
    setForm({ name: acc.name, type: acc.type, description: acc.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const body = { name: form.name, type: form.type, description: form.description || undefined };
    if (editing) {
      await api.put(`/api/accounts/${editing.id}`, body);
    } else {
      await api.post('/api/accounts', body);
    }
    setDialogOpen(false);
    load();
  };

  const handleDelete = async (id: string) => {
    if (confirm('Poistetaanko tämä tili?')) {
      await api.delete(`/api/accounts/${id}`);
      load();
    }
  };

  const typeColor = (type: string) => {
    switch (type) {
      case 'bank': return 'primary';
      case 'credit': return 'warning';
      case 'cash': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600} sx={{ flexGrow: 1 }}>
          Tilit
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>
          Lisää
        </Button>
      </Box>

      {accounts.map((acc) => (
        <Card key={acc.id} sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box display="flex" alignItems="center">
              <Box sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontWeight={600}>{acc.name}</Typography>
                  <Chip label={acc.type} size="small" color={typeColor(acc.type) as any} />
                </Box>
                {acc.description && (
                  <Typography variant="body2" color="text.secondary">
                    {acc.description}
                  </Typography>
                )}
              </Box>
              <IconButton size="small" onClick={() => openEdit(acc)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(acc.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      ))}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Muokkaa tiliä' : 'Uusi tili'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Nimi"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Tyyppi</InputLabel>
              <Select
                value={form.type}
                label="Tyyppi"
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <MenuItem value="bank">Pankkitili</MenuItem>
                <MenuItem value="credit">Luottotili</MenuItem>
                <MenuItem value="cash">Käteinen</MenuItem>
                <MenuItem value="other">Muu</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Kuvaus"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Peruuta</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>
            Tallenna
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
